'use client'
import dynamic from 'next/dynamic'
import { BffProduct, Product } from '../../types/product'
import { AnalyticsTracker } from '../../components/analytics-tracker'
import { Layout, LayoutProps } from '../../components/layout'
import { TemplateSelector, TemplateSelectorProps } from '../../components/template-selector'
import {
  SanityInitialPageQuery,
  SanityNonBookableDates,
  SanitySmProduct,
  SanityTemplate,
} from '../../services/sanity/types'
import { mapLayoutProps, mapPageDetailPageProps, mapProductListing, mapSelector } from '../../services/sanity/mappers'
import {
  CtaAnalyticsParams,
  CtaAnalyticsProvider,
  GeoCityProvider,
  TextBlockImageRendererProvider,
} from '@studio/atomic-ui'
import { mapImage } from '../../services/sanity/client'
import analyticsService from '../../analytics/analytics.instance'
import { isValidHttpURL } from '../../utils/helpers'
import useTrackPageViews from '../../hooks/useTrackPageViews'
import { setUtmFields } from '../../utils/analytics'
import { ensureTemplateVariantCookie, ensureModuleVariantCookies } from '../../utils/ab-testing'
import { PlpSortStrategy } from '../../services/api/types'
import { ensurePlpSortCookie } from '../../utils/plp-sort-cookie'
import { usePathname, useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useState } from 'react'
import { useGeoCity } from '../../hooks/useGeoCity'
import { QueryClient, QueryClientProvider } from 'react-query'
import { FaroErrorBoundary } from '@grafana/faro-react'
import { FaroInjector } from '@tishmanspeyer/observability/frontend'
import type { Building } from '../../types/building'
import { BuildingsProvider, useBuildings } from '../../components/buildings-provider'

type ClientPageProps = {
  buildingDetails?: any
  pdp?: any
  product?: BffProduct | Product
  payload?: any
  exists?: SanityInitialPageQuery
  holidays?: SanityNonBookableDates
  initialProducts?: SanitySmProduct[]
  buildings: Building[]
}

export const ClientPage = ({ buildings, ...props }: ClientPageProps) => {
  // Synchronous on purpose (not an effect): must be set before any child renders. Idempotent.
  analyticsService?.setBuildings(buildings)

  return (
    <BuildingsProvider buildings={buildings}>
      <PageContent {...props} />
    </BuildingsProvider>
  )
}

const PageContent = (props: Omit<ClientPageProps, 'buildings'>) => {
  const { buildingDetails, pdp, product, exists, payload, holidays, initialProducts } = props
  const { buildings } = useBuildings()

  // Ensure A/B variant cookies are set early so both layout and template selection use the same variants
  useEffect(() => {
    ensureTemplateVariantCookie(payload?.abTestingConfiguration)
    ensureModuleVariantCookies(payload?.moduleABTestingConfiguration)
    ensurePlpSortCookie(payload?.plpSortConfig)
  }, [payload?.abTestingConfiguration, payload?.moduleABTestingConfiguration, payload?.plpSortConfig])

  if (exists?.product) {
    const data = {
      ...mapLayoutProps({ ...pdp, title: pdp.product.productName }),
      type: 'tmPdp' as SanityTemplate,
      template: mapPageDetailPageProps(
        {
          ...pdp.product,
          buildingDetails,
        },
        product as Product,
        buildings
      ),
      product: null,
    }
    return <View {...data} />
  }

  if (exists?.template === 'tmProductListing') {
    const plpSortConfig = payload?.plpSortConfig
    const data = {
      ...mapLayoutProps(payload),
      type: exists.template,
      template: {
        ...mapProductListing(payload.template, payload.title),
        initialProducts,
        sortStrategy: plpSortConfig?.sortStrategy ?? PlpSortStrategy.Standard,
      },
      product: null,
    }

    return <View {...data} />
  }

  if (exists?.template) {
    // Determine selected template type (considering A/B testing variants)
    const selectedType = (payload?.abTestingConfiguration?.selectedTemplateType as SanityTemplate) || exists.template

    if ((exists.template === 'tmCheckout' || exists.template === 'tmGuestList') && product) {
      if (product && (product as Product)?.slug && payload && payload?.header && payload?.header?.alternateLink) {
        payload.header.alternateLink.href = `/${(product as Product)?.slug}`
      }

      const data = {
        ...mapLayoutProps(payload),
        type: selectedType,
        template: mapSelector(
          exists.template,
          {
            ...payload,
            template: { ...(payload.template as object), holidays },
          },
          buildings
        ),
        product: product as Product,
      }

      return <View {...data} />
    }

    const data = {
      ...mapLayoutProps(payload),
      type: selectedType,
      template: mapSelector(exists.template, payload, buildings),
      product: product as Product,
    }

    return <View {...data} />
  }
  if (payload) {
    const data = {
      ...mapLayoutProps(payload),
      type: 'tmFlexible' as const,
      template: mapSelector('tmFlexible', payload, buildings),
      product: null,
    }
    return <View {...data} />
  }
}

const View = (props: LayoutProps & TemplateSelectorProps) => {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const [queryClient] = useState(() => new QueryClient())
  const geoCity = useGeoCity()

  useTrackPageViews(props?.abTestingConfiguration, props?.moduleABTestingConfiguration)

  useEffect(() => {
    if (!pathname) return
    const query: Record<string, string> = {}
    searchParams?.forEach((value, key) => {
      query[key] = value
    })
    setUtmFields(query)
  }, [pathname, searchParams])

  const logCtaClick = useCallback(({ text, style, href, component }: CtaAnalyticsParams) => {
    const pageUrl = `${location.protocol}//${location.host}${location.pathname}`
    const formattedHref = isValidHttpURL(href)
      ? href
      : `${location.protocol}//${location.host}${href.replace('//', '/')}`
    analyticsService?.trackCtaClick({
      text,
      style,
      href: href ? formattedHref : pageUrl,
      component,
      pageUrl,
    })
  }, [])

  return (
    <>
      <FaroInjector />
      <FaroErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <TextBlockImageRendererProvider render={mapImage}>
            <CtaAnalyticsProvider logClick={logCtaClick}>
              <GeoCityProvider value={geoCity}>
                <Layout title={props.title} navbar={props.navbar} footer={props.footer}>
                  <TemplateSelector type={props.type} template={props.template} product={props.product} />
                  <AnalyticsTracker
                    abTestingConfiguration={props.abTestingConfiguration}
                    moduleABTestingConfiguration={props.moduleABTestingConfiguration}
                    pageType={props.pageType}
                    id={props.id}
                    title={props.title}
                  />
                </Layout>
              </GeoCityProvider>
            </CtaAnalyticsProvider>
          </TextBlockImageRendererProvider>
        </QueryClientProvider>
      </FaroErrorBoundary>
    </>
  )
}
